/**
 * ENG-9694 — per-agent standing rules, rendered into CLAUDE.md.
 *
 * WHY THIS EXISTS. A customer told us he could not work out how to turn a
 * repeated instruction into a standing rule, and kept re-issuing it
 * conversationally instead. The mechanisms we have are skills, routines and
 * scheduled tasks — and a skill AUTO-ACTIVATES on its frontmatter description
 * matching the task at hand, which means it can silently fail to fire. So
 * "always sign off as the Dig team" has had no reliable home: as a skill it is
 * one imprecise description away from not applying, with nothing to notice.
 *
 * A rule here is the other trade. It is resident in CLAUDE.md on every single
 * turn, so it cannot fail to load — and that is precisely what it costs.
 *
 * ── THE COST, STATED PLAINLY, BECAUSE THE CAPS COME OUT OF IT ──
 *
 * ENG-9694's own write-up argues "a rule that fires every time costs no context
 * and cannot be forgotten". The first half is true of SKILLS, which load on
 * demand. It is the opposite of true here: a CLAUDE.md rule cannot be forgotten
 * BECAUSE it is resident, and every character is re-sent on every request for
 * the life of the agent.
 *
 * That is why this file has hard caps and why they are derived rather than
 * chosen. {@link CLAUDE_MD_BUDGET_CHARS} is a CI-enforced ratchet whose own
 * comment says "Do NOT raise either number to make a failing budget test pass.
 * Condense content instead." A user-editable section with no ceiling is a
 * standing invitation to breach it — and the users most motivated to fill it are
 * the ones who already feel unheard, which is exactly the population this
 * feature is for.
 *
 * ── WHY A LENGTH CAP AND NOT JUST A COUNT ──
 *
 * Eight rules of 2,000 characters each is not eight rules, it is a document. The
 * per-rule cap is the line between "a standing instruction" and "a procedure",
 * and a procedure belongs in a skill where it costs nothing until it is needed.
 * The cap is therefore a routing decision as much as a budget one: hitting it is
 * the signal that what you have is a skill.
 */

import { CLAUDE_MD_BUDGET_CHARS } from './identity.js';
import { LITERAL_SECRET_PATTERNS } from '../../mcp-secret-lint.js';

/** Marker convention shared with the integrations block, so a refresh can
 *  replace this section surgically rather than by re-generating the document. */
export const AGENT_RULES_SECTION_START = '<!-- AGT:RULES_START -->';
export const AGENT_RULES_SECTION_END = '<!-- AGT:RULES_END -->';

/**
 * Most rules one agent may carry.
 *
 * A human limit before it is a budget one — a list nobody can hold in their head
 * is a list nobody audits, and an unaudited always-on instruction is how an
 * agent quietly keeps doing something everyone has forgotten agreeing to.
 *
 * EIGHT RATHER THAN THE TEN ORIGINALLY PROPOSED, and the reason is worth
 * recording because it is the discipline this file is about. Ten at 200
 * characters is 2,040 chars, and the budget-share test asserts this section
 * stays under 5% of {@link CLAUDE_MD_BUDGET_CHARS} — 1,900. My first version
 * failed its own assertion.
 *
 * The tempting fix was to widen the assertion to 6%. That is precisely the move
 * `identity.ts` forbids for the budget itself: "Do NOT raise either number to
 * make a failing budget test pass. Condense content instead." The share was the
 * arbitrary number here; the per-rule cap is the considered one, because it is
 * the instruction-versus-procedure boundary. So the count moved, not the bar.
 *
 * Eight is still a list a person can hold in their head, which was the whole
 * argument for a count limit.
 */
export const MAX_AGENT_RULES = 8;

/**
 * Longest a single rule may be.
 *
 * 200 characters is roughly two sentences: enough for "always sign off as the
 * Dig team, never use my first name" and not enough for a procedure. See the
 * header for why that boundary is the point rather than a limitation.
 */
export const MAX_AGENT_RULE_CHARS = 200;

/**
 * The worst case this section can contribute to CLAUDE.md.
 *
 * Derived, not asserted — if either cap moves, this moves with it and the
 * budget test below re-checks the claim. That is deliberate: a hand-written
 * "about 2KB" comment would rot the first time someone raised a cap.
 */
export const MAX_AGENT_RULES_CHARS = MAX_AGENT_RULES * (MAX_AGENT_RULE_CHARS + 4);

/** A single standing rule. `id` is the storage id, so removal can name one. */
export interface AgentRule {
  id: string;
  text: string;
  /**
   * ENG-10194: when the rule was stored, ISO-8601. OPTIONAL, and its optionality
   * is the design rather than laziness — every consumer in this module
   * (`agentRulesRestartBasis`, `buildAgentRulesSection`, the describers) reads
   * `text` and `id` only, so a rule without a timestamp behaves exactly as it
   * did before and the field cannot become load-bearing by accident.
   *
   * WHY IT IS NOT RENDERED INTO CLAUDE.md. `agentRulesRestartBasis` is computed
   * from rule TEXT alone, deliberately — see its own comment on why keying the
   * restart decision to the file would respawn every session on every unrelated
   * regeneration. An age in the resident section would change the rendered file
   * every single day while the basis stayed identical, so the agent's charter
   * would churn with nothing to show for it. The age is surfaced through
   * `rule_list` instead, which is generated on demand and persisted nowhere.
   *
   * WHY AGE AND NOT AN EXPIRY. `agent_rules` has no `expires_at` and that is a
   * decision, not an omission: the migration header states it outright — "a
   * standing rule that quietly stops applying is the precise failure ENG-9790
   * was written to prevent", and a TTL is an automatic invisible removal. Age
   * is the opposite shape. It removes nothing and tells the reader how long a
   * rule saying "until Brad lifts this" has been waiting.
   */
  createdAt?: string;
}

export type AgentRuleRejection =
  | { ok: false; reason: 'empty' }
  | { ok: false; reason: 'too_long'; max: number; actual: number }
  // ENG-9789 / CodeRabbit on #5342: rule text that carries a credential.
  | { ok: false; reason: 'looks_like_secret'; kind: string }
  // ENG-9790: `current` makes a cap refusal actionable — the caller needs the
  // list to ask "which of these should I drop?". Optional so `validateAgentRule`,
  // which is given only a COUNT, can still produce this rejection.
  | { ok: false; reason: 'too_many'; max: number; current?: readonly AgentRule[] }
  | { ok: false; reason: 'unknown_replace'; replaceId: string; current: readonly AgentRule[] };

export type AgentRuleValidation = { ok: true; text: string } | AgentRuleRejection;


/**
 * Does this rule text carry something credential-shaped?
 *
 * CodeRabbit raised this against the audit diff — `body` is arbitrary user text
 * landing in an org-readable `audit_log` row, and `scrubValue`'s patterns are
 * START-ANCHORED, so `Always use xoxb-...` passes straight through and the
 * credential becomes durable.
 *
 * The finding is right, but the audit log is the smallest of the three places it
 * would end up, which is why this rejects at the WRITE BOUNDARY instead of
 * redacting downstream. A rule is:
 *
 *   1. stored in `agent_rules`,
 *   2. rendered VERBATIM into CLAUDE.md, resident on every single request, and
 *   3. copied into the audit diff.
 *
 * Redacting (3) would leave the credential sitting in (1) and (2) — including in
 * the agent's own prompt, which is the worst of the three. Refusing it outright
 * is the only fix that covers all of them, and it is also the kinder answer:
 * the person finds out immediately rather than discovering later that a secret
 * has been baked into an agent's instructions.
 *
 * Reuses {@link LITERAL_SECRET_PATTERNS}, the repo's existing catalogue, so a
 * new provider shape added for the `.mcp.json` lint protects rules too. Their
 * anchors are deliberately stripped: that lint inspects a whole config VALUE,
 * where `^` is correct, whereas a rule is prose and the token appears mid
 * sentence. Same shapes, different haystack.
 */
function credentialShapeIn(text: string): string | null {
  for (const { name, re } of LITERAL_SECRET_PATTERNS) {
    const unanchored = new RegExp(re.source.replace(/^\^/, ''), re.flags.replace('g', ''));
    if (unanchored.test(text)) return name;
  }
  return null;
}

/**
 * Validate a proposed rule against the caps.
 *
 * Returns a structured rejection rather than throwing, because every caller is
 * a surface that has to EXPLAIN the refusal to a person — "that is more of a
 * skill than a rule" is a useful sentence, and an exception is not.
 *
 * Trims first: leading/trailing whitespace is not content, and counting it
 * toward a cap would reject a rule for being indented.
 */
export function validateAgentRule(
  text: string,
  existingCount: number,
): AgentRuleValidation {
  const trimmed = text.trim();
  if (!trimmed) return { ok: false, reason: 'empty' };
  if (trimmed.length > MAX_AGENT_RULE_CHARS) {
    return { ok: false, reason: 'too_long', max: MAX_AGENT_RULE_CHARS, actual: trimmed.length };
  }
  const secret = credentialShapeIn(trimmed);
  if (secret) return { ok: false, reason: 'looks_like_secret', kind: secret };
  // Checked AFTER the per-rule checks so a user who is both over the count and
  // writing an essay is told about the essay — the actionable half, and the one
  // that suggests a skill instead.
  if (existingCount >= MAX_AGENT_RULES) {
    return { ok: false, reason: 'too_many', max: MAX_AGENT_RULES };
  }
  return { ok: true, text: trimmed };
}

/**
 * ENG-9790 — the named swap.
 *
 * WHY THIS EXISTS. `validateAgentRule` returns `too_many` at the cap and stops
 * there, which makes a full list a DEAD END: the ninth rule simply fails and the
 * person has no move. That is the wrong behaviour for a cap that exists for
 * budget reasons rather than policy ones — we are not saying "you may not have
 * this rule", we are saying "not all of them at once", and those need different
 * answers.
 *
 * ── WHY A SWAP AND NOT A DELETE ──
 *
 * An agent ADDING a rule and an agent DELETING one are different authorities,
 * and the asymmetry is in how the mistakes surface:
 *
 *   a bad add    → VISIBLE. The agent starts doing something odd and someone
 *                  notices, because the behaviour is new.
 *   a bad delete → INVISIBLE. The user goes on believing "always CC me on client
 *                  emails" is in force. Nothing looks different; the rule just
 *                  stops applying, and they find out when it matters.
 *
 * This file already argues that an unaudited always-on instruction is how an
 * agent quietly keeps doing something everyone forgot agreeing to. Quietly
 * STOPPING is the mirror of that and it is the harder one to catch. It is also
 * the shape of ENG-8562, where the charter stated a hold was in place that was
 * not: a confident claim about state that is not true.
 *
 * So the eviction is not prevented, it is made UNSAYABLE WITHOUT NAMING. There
 * is deliberately no `removeAgentRule` in this module and no code path that
 * drops a rule the caller did not identify. That is a structural guarantee
 * rather than a convention — you cannot forget to name the victim, because
 * there is no overload that lets you.
 *
 * ── ORDERING: DELIBERATELY NOT MODELLED ──
 *
 * "Replace it with a more important rule" implies a ranking. There is none, and
 * this does not add one. Importance is expressed AT EVICTION TIME by naming what
 * goes, not persisted as a priority field.
 *
 * A stored priority is a second thing to get wrong and it goes stale: you set it
 * once while you cared, and it silently misrepresents your intent a month later.
 * That is the unaudited-list problem the caps exist to prevent, with extra
 * steps. Because the swap is explicit and surfaced, the ranking judgement is
 * visible and correctable at the moment it is made — better than a number nobody
 * maintains.
 */
export type AgentRulePlan = {
  ok: true;
  /** The normalised text to store. */
  text: string;
  /**
   * The rule this add displaces, or `undefined` when there was room.
   *
   * Carries the full rule rather than its id because this is what an audit
   * record and the user-facing sentence both need, and re-reading it after the
   * write has already lost it. Callers MUST record it and MUST tell the user.
   */
  evicted?: AgentRule;
};

export type AgentRulePlanResult = AgentRulePlan | AgentRuleRejection;

/**
 * Plan an add, optionally naming the rule it replaces.
 *
 * `replaceId` is honoured whether or not the list is full: "swap Y for X" is a
 * legitimate intent with room to spare, and refusing it there would push the
 * caller toward a delete-then-add pair — two writes, with a window in between
 * where the user has neither rule and nothing says so.
 */
export function planAgentRuleAdd(
  text: string,
  existing: readonly AgentRule[],
  replaceId?: string,
): AgentRulePlanResult {
  const trimmed = text.trim();
  if (!trimmed) return { ok: false, reason: 'empty' };
  if (trimmed.length > MAX_AGENT_RULE_CHARS) {
    return { ok: false, reason: 'too_long', max: MAX_AGENT_RULE_CHARS, actual: trimmed.length };
  }
  const secret = credentialShapeIn(trimmed);
  if (secret) return { ok: false, reason: 'looks_like_secret', kind: secret };

  if (replaceId !== undefined) {
    const victim = existing.find((r) => r.id === replaceId);
    // A replaceId that matches nothing is REFUSED, never ignored. Ignoring it
    // would silently turn an intended swap into a plain add — which then either
    // fails on the cap for a reason the caller did not ask about, or succeeds
    // and leaves the rule they meant to retire still in force. Both end with the
    // user believing something about their rules that is not true.
    if (!victim) {
      return { ok: false, reason: 'unknown_replace', replaceId, current: existing };
    }
    return { ok: true, text: trimmed, evicted: victim };
  }

  if (existing.length >= MAX_AGENT_RULES) {
    // Carries the current rules so the refusal is ACTIONABLE. The caller cannot
    // offer "which of these should I drop?" without them, and sending the person
    // away to find their own rule list is how a cap becomes a dead end.
    return { ok: false, reason: 'too_many', max: MAX_AGENT_RULES, current: existing };
  }
  return { ok: true, text: trimmed };
}

/**
 * The sentence to say to the person, for every outcome.
 *
 * Lives here rather than in each surface so a refusal cannot be explained two
 * different ways by two callers, and so the LENGTH refusal always carries its
 * routing advice: hitting the per-rule cap is the signal that what you have is a
 * SKILL, which loads on demand and costs nothing until it is needed. "Too long"
 * on its own leaves the person to guess, and the guess is usually "write it
 * shorter" — which produces a worse rule rather than a better home for it.
 */
export function describeAgentRuleOutcome(result: AgentRulePlanResult): string {
  if (result.ok) {
    return result.evicted
      ? `Added the rule, and dropped "${result.evicted.text}" to make room. Tell me if you want that one back.`
      : `Added the rule.`;
  }
  switch (result.reason) {
    case 'empty':
      return `That rule is empty, so there is nothing to store.`;
    case 'looks_like_secret':
      // Say WHY, because "rejected" alone invites a workaround (splitting the
      // token, or putting it somewhere worse). A rule is resident in my prompt
      // on every request, so a credential in one is not a stored secret, it is
      // a published one.
      return `That looks like it contains a credential (${result.kind}), so I have not saved it. A rule is copied into my instructions verbatim and sent on every single request, so a secret in one would be far more exposed than one sitting in a password manager. Tell me where the credential lives instead and I will look it up when I need it.`;
    case 'too_long':
      return `That is ${result.actual} characters and a rule can be ${result.max}. Rules are resident in my instructions on every single turn, so they have to stay short. Something this long is really a skill: it can be as long as it needs to be, and it costs nothing until the task it applies to comes up. Want me to make it one instead?`;
    case 'too_many': {
      // `current` is absent on the count-only path (`validateAgentRule`, which is
      // handed a number and cannot know the rules). Degrade to the generic ask
      // rather than printing "undefined" at a person — and note the difference is
      // exactly why `planAgentRuleAdd` takes the LIST: the richer refusal is the
      // one that can offer the swap.
      const list = result.current?.map((r) => `"${r.text}"`).join(', ');
      return list
        ? `I am already holding my limit of ${result.max} standing rules. Tell me which one to drop and I will swap this in: ${list}.`
        : `I am already holding my limit of ${result.max} standing rules. Tell me which one to drop and I will swap this in.`;
    }
    case 'unknown_replace':
      return `I could not find the rule you asked me to replace, so I have changed nothing. My current rules are: ${result.current.map((r) => `"${r.text}"`).join(', ')}.`;
  }
}

/**
 * ENG-10194: the note appended to a successful `rule_add`.
 *
 * Said at ADD time because that is the only moment anyone knows whether the
 * rule was meant to be temporary. `agent_rules` has no `expires_at` and
 * deliberately never will — the migration header states it outright: "a
 * standing rule that quietly stops applying is the precise failure ENG-9790 was
 * written to prevent", and a TTL is an automatic invisible removal. So the end
 * cannot live in a column; it has to live in the rule's own words, where a
 * person reading it back months later can see it.
 *
 * The live example this exists for: "PAUSED by Brad 2026-09-07: do not assign
 * any new issues to coding agents ... until Brad lifts this." Resident in the
 * agent's instructions on every turn, suppressing work, with nothing anywhere
 * to notice if Brad forgets. It got the wording right by accident; nothing
 * asked for it.
 *
 * Exported as a constant rather than inlined at the call site so the assertion
 * in the test is against the shipped string and not a copy of it.
 */
export const AGENT_RULE_NO_END_DATE_NOTE =
  ' Rules have no end date: this one applies on every turn until someone removes it.' +
  ' If it is only meant to hold for a while, say so IN the rule ("until the migration ships")' +
  ' so the reason to drop it is visible later — rule_list shows how old each rule is.';

/**
 * ENG-10194: render the `rule_list` reply, ages and all.
 *
 * Pure and exported for the ENG-9862 reason. That ticket is the precedent worth
 * carrying: `buildAgentRulesSection` rendered perfectly for weeks while its only
 * production caller never passed it the rules, so the section was empty on every
 * agent — stored, listed back, reported as in force, applied on no turn. A
 * renderer nobody can call in a test is a renderer nobody notices is unwired.
 *
 * `createdAt` is OPTIONAL on the way in, and that is the mixed-version contract
 * rather than defensiveness: an `agt` CLI older than this change is served by
 * the same API, gets rules with no timestamp, and prints the list exactly as it
 * always did. There is no version to coordinate.
 */
export function describeAgentRuleList(
  rules: readonly AgentRule[],
  max: number,
  maxChars: number,
  now: number = Date.now(),
): string {
  if (rules.length === 0) {
    return `No standing rules yet. You can hold up to ${max}, each up to ${maxChars} characters.`;
  }
  const lines = rules
    .map((r) => {
      const days = agentRuleAgeInDays(r.createdAt, now);
      if (days === null) return `- ${r.text}  (id: ${r.id})`;
      const age = days === 0 ? 'added today' : `added ${days} day${days === 1 ? '' : 's'} ago`;
      return `- ${r.text}  (id: ${r.id}, ${age})`;
    })
    .join('\n');

  // The ages are useless without the sentence that says why they are there.
  // Unconditional rather than fired past some threshold: any threshold is a
  // number nobody chose, and the rule that matters is always the one just under
  // it.
  const note =
    ' Rules never expire — nothing will remove one for you. If any of these was meant to be' +
    ' temporary ("until X lifts this", "while we are mid-migration"), check with the person' +
    ' whether it still applies, and use rule_remove with a reason if it does not.';

  return `${rules.length} of ${max} standing rules in force:\n${lines}\n${note}`;
}

/**
 * Whole days since a rule was stored, or null when there is no readable
 * timestamp.
 *
 * Clamped at zero: host and database clocks disagree by seconds routinely, and
 * "added -1 days ago" would read as a bug in the rule rather than in the clock.
 */
export function agentRuleAgeInDays(createdAt: string | undefined, now: number = Date.now()): number | null {
  if (!createdAt) return null;
  const t = Date.parse(createdAt);
  if (Number.isNaN(t)) return null;
  return Math.max(0, Math.floor((now - t) / (24 * 60 * 60 * 1000)));
}

/**
 * The outcome of applying a plan. A structured result rather than a throw,
 * matching the rest of this module: every caller is a surface that has to
 * EXPLAIN itself to a person, and an exception is not a sentence.
 */
export type AgentRuleApplyResult =
  | { ok: true; rules: AgentRule[]; evicted?: AgentRule }
  | { ok: false; reason: 'cap_reached'; max: number }
  | { ok: false; reason: 'victim_missing'; replaceId: string }
  | { ok: false; reason: 'victim_ambiguous'; replaceId: string; matches: number };

/**
 * Apply a plan, returning the new rule list.
 *
 * Takes the PLAN rather than a rule id so that the only way to remove a rule via
 * an ADD is to have gone through {@link planAgentRuleAdd} and named it. There is
 * no argument shape here that expresses "drop this one and add nothing", and
 * that is still true and still deliberate.
 *
 * ENG-9860 added {@link planAgentRuleRemove} as a SEPARATE path for a rule that
 * turned out to be wrong. It does not weaken this one: a cap refusal still
 * cannot be resolved by a silent delete, because removal there requires its own
 * call with its own required reason and its own audit event.
 *
 * ── WHY IT RE-VALIDATES (CodeRabbit on #5337) ──
 *
 * My first version trusted the plan and just filtered-and-appended. A plan is a
 * snapshot of a decision, and `existing` is read again HERE — so between the two
 * the world can move, and every way it moves ends with the user believing
 * something untrue about their own rules, which is the exact failure this whole
 * ticket exists to prevent:
 *
 *   - A no-eviction plan made when there WAS room, applied once the list is
 *     full, appends a 9th rule. The cap is not advisory: it is what keeps the
 *     resident section inside a shrink-only budget, so silently exceeding it
 *     puts the whole CLAUDE.md over its ratchet.
 *   - A victim that is no longer there (someone else already removed it) makes
 *     the filter a no-op, so we append anyway — over cap — while the caller
 *     cheerfully reports "dropped Y". Y was already gone; the user is told a
 *     removal happened that did not.
 *   - Duplicate ids would remove TWO rules for one named eviction. That is the
 *     silent delete this design exists to make impossible, arriving through the
 *     back door.
 *
 * So the plan is re-checked against the list it is actually being applied to.
 * Rejecting a stale plan is always safe — the caller re-plans against fresh
 * state and the user is asked again — whereas applying one is not.
 */
export function applyAgentRulePlan(
  plan: AgentRulePlan,
  existing: readonly AgentRule[],
  newId: string,
): AgentRuleApplyResult {
  if (!plan.evicted) {
    // Re-checked rather than trusted: `planAgentRuleAdd` saw a different list.
    if (existing.length >= MAX_AGENT_RULES) {
      return { ok: false, reason: 'cap_reached', max: MAX_AGENT_RULES };
    }
    return { ok: true, rules: [...existing, { id: newId, text: plan.text }] };
  }

  const replaceId = plan.evicted.id;
  const matches = existing.filter((r) => r.id === replaceId);
  if (matches.length === 0) return { ok: false, reason: 'victim_missing', replaceId };
  // Ambiguity is refused, never resolved by picking one: ids are supposed to be
  // unique, so duplicates mean the store is in a state we do not understand, and
  // guessing which to drop is precisely the unnamed eviction this forbids.
  if (matches.length > 1) {
    return { ok: false, reason: 'victim_ambiguous', replaceId, matches: matches.length };
  }

  const kept = existing.filter((r) => r.id !== replaceId);
  return { ok: true, rules: [...kept, { id: newId, text: plan.text }], evicted: matches[0] };
}

/**
 * ENG-9860 — the exit for a rule that turns out to be WRONG.
 *
 * ── WHAT THIS DOES NOT CHANGE ──
 *
 * `applyAgentRulePlan` above says "there is no argument shape here that
 * expresses drop this one and add nothing", and the swap-only constraint it
 * describes is RIGHT. It is what stops the list rotting into rules everyone
 * assumes are live and nobody has read in a month, and nothing here relaxes it
 * for the ADD path. A cap refusal still cannot be resolved by a silent delete.
 *
 * ── THE CASE IT DID NOT ANTICIPATE ──
 *
 * A rule that is actively WRONG: factually incorrect, harmful, or written
 * against a workflow that has since changed. Swap-only leaves the operator two
 * moves and both are bad. Replace it with a rule they do not want, and there are
 * now two problems instead of one, the second also resident on every turn. Or
 * leave it, and a known-wrong instruction steers every single request until
 * someone thinks of a rule worth its slot. Neither is anyone's intent; both are
 * artefacts of the constraint.
 *
 * The asymmetry that makes this urgent rather than tidy: a wrong SKILL is wrong
 * occasionally, because it only loads when its description matches the task. A
 * wrong RULE is wrong constantly. The cost of leaving one in place is unbounded.
 *
 * ── HOW THE ORIGINAL PRINCIPLE SURVIVES ──
 *
 * The stated worry is a rule that QUIETLY stops applying. Read precisely, that
 * is an argument against silence, not against removal — so this makes the
 * removal loud instead of making it impossible:
 *
 *   1. `reason` is REQUIRED and non-blank. A removal with nothing attached is
 *      exactly what the current design protects against; one with a stated
 *      reason is not. Same bar as `kanban_reopen`'s feedback.
 *   2. The caller MUST tell the person which rule stopped applying, in the same
 *      reply — the identical obligation the `replace_id` path already carries.
 *      That obligation is what delivers the "nobody notices" protection. The
 *      absence of a tool never delivered it; it only delivered "nobody can".
 *   3. The removal lands in the audit trail carrying the rule IN FULL, so
 *      "which rules were in force when this happened" stays answerable after
 *      the row is gone.
 *
 * Under that shape no rule ever silently stops applying, which is the property
 * the design actually cares about. What changes is that evicting a bad rule no
 * longer requires inventing a good one.
 */
export type AgentRuleRemovalRejection =
  | { ok: false; reason: 'reason_required' }
  | { ok: false; reason: 'reason_too_long'; max: number; actual: number }
  // The reason is arbitrary user text bound for an org-readable audit row, so it
  // gets the same write-boundary refusal the rule body does. Narrower blast
  // radius than a body (it is never rendered into CLAUDE.md), but a credential
  // durable in audit_log is still a credential we chose to store.
  | { ok: false; reason: 'reason_looks_like_secret'; kind: string }
  | { ok: false; reason: 'unknown_rule'; ruleId: string; current: readonly AgentRule[] }
  | { ok: false; reason: 'rule_ambiguous'; ruleId: string; matches: number };

export type AgentRuleRemovalPlan = {
  ok: true;
  /** The rule being retired, in full — the audit record and the sentence both need it. */
  removed: AgentRule;
  /** Normalised, non-blank. Stored on the audit event, not on the row (the row is gone). */
  reason: string;
  /** The list after removal. May legitimately be empty — see below. */
  remaining: AgentRule[];
};

export type AgentRuleRemovalResult = AgentRuleRemovalPlan | AgentRuleRemovalRejection;

/** Mirrors MAX_AGENT_RULE_CHARS: an audit field, not an essay field. */
export const MAX_AGENT_RULE_REMOVAL_REASON_CHARS = 200;

/**
 * Plan a removal.
 *
 * ── REMOVING THE LAST RULE IS ALLOWED, DELIBERATELY ──
 *
 * Zero rules is a legitimate and legible state: it is what every agent starts
 * in, and `buildAgentRulesSection` already renders '' for it rather than an
 * empty heading. Refusing the last removal would recreate this exact ticket at
 * n=1 — an operator holding one known-wrong rule with no way out — which is the
 * trap, not a guard against it.
 */
export function planAgentRuleRemove(
  ruleId: string,
  reason: string,
  existing: readonly AgentRule[],
): AgentRuleRemovalResult {
  const why = reason.trim();
  if (!why) return { ok: false, reason: 'reason_required' };
  if (why.length > MAX_AGENT_RULE_REMOVAL_REASON_CHARS) {
    return {
      ok: false,
      reason: 'reason_too_long',
      max: MAX_AGENT_RULE_REMOVAL_REASON_CHARS,
      actual: why.length,
    };
  }
  const secret = credentialShapeIn(why);
  if (secret) return { ok: false, reason: 'reason_looks_like_secret', kind: secret };

  const matches = existing.filter((r) => r.id === ruleId);
  // Refused, never treated as already-done. "It was not there" and "I removed
  // it" are different facts, and reporting the second for the first tells the
  // person a rule stopped applying when something else is still in force.
  if (matches.length === 0) {
    return { ok: false, reason: 'unknown_rule', ruleId, current: existing };
  }
  // Same reasoning as applyAgentRulePlan's victim_ambiguous: duplicate ids mean
  // the store is in a state we do not understand, and picking one is the
  // unnamed deletion this whole design forbids.
  if (matches.length > 1) {
    return { ok: false, reason: 'rule_ambiguous', ruleId, matches: matches.length };
  }

  return {
    ok: true,
    removed: matches[0]!,
    reason: why,
    remaining: existing.filter((r) => r.id !== ruleId),
  };
}

/**
 * The sentence to say to the person.
 *
 * The success case QUOTES THE RULE rather than naming its id, for the same
 * reason `describeAgentRuleOutcome` does on the swap path: an id is not
 * something anyone recognises, and "I removed rule 7f3a…" does not let a person
 * notice that the wrong one went.
 */
export function describeAgentRuleRemoval(result: AgentRuleRemovalResult): string {
  if (result.ok) {
    const left = result.remaining.length;
    const tail =
      left === 0
        ? ` You now have no standing rules.`
        : ` ${left} standing rule${left === 1 ? '' : 's'} still apply.`;
    return `Removed the rule "${result.removed.text}", so it no longer applies. Reason: ${result.reason}.${tail}`;
  }
  switch (result.reason) {
    case 'reason_required':
      return `I need a reason before I drop a rule — a rule that vanishes with nothing recorded is the thing this is meant to prevent.`;
    case 'reason_too_long':
      return `That reason is ${result.actual} characters and the limit is ${result.max}. A short "why" is all that is needed.`;
    case 'reason_looks_like_secret':
      return `That reason looks like it contains a ${result.kind}, so I have not stored it. Rewrite it without the credential.`;
    case 'unknown_rule':
      return result.current.length
        ? `I have no rule with that id, so I have changed nothing. The rules I do have are: ${result.current.map((r) => `"${r.text}"`).join(', ')}.`
        : `I have no standing rules at all, so there is nothing to remove.`;
    case 'rule_ambiguous':
      return `That id matches ${result.matches} rules, which should be impossible. I have changed nothing — this needs a person to look.`;
  }
}

/**
 * ENG-9861 — the canonical basis for "have this agent's standing rules changed
 * in a way the RUNNING session has not seen?"
 *
 * THE PROBLEM. ENG-9862 fixed rules never reaching CLAUDE.md at all. They now
 * render — but CLAUDE.md is read ONCE at session start, so a rule added
 * mid-session sits on disk while the running agent keeps its old instructions.
 * The person is told the rule is in force and it applies on no turn until an
 * unrelated restart. Same silent-success shape as ENG-9802, where an allowlist
 * converged to disk while the live MCP kept enforcing the old one.
 *
 * A string rather than a hash, deliberately: this package has no business
 * choosing a digest, and the CLI already hashes `telegramPolicyEnvBasis` the
 * same way. Keeping the basis pure also makes the interesting cases provable
 * without the 21k-line worker.
 *
 * ── WHY THE TEXTS AND NOT THE ROWS ──
 *
 * Ids are excluded. What reaches the agent is the rendered text; an id is
 * invisible to it. Including ids would respawn a live session for a swap that
 * produced byte-identical instructions — a restart delivering nothing, which is
 * the churn ENG-5932 argues against.
 *
 * ── WHY SORTED ──
 *
 * ENG-7435: an unordered list oscillated a hash into ~10-minute respawns. Rules
 * are read `ORDER BY created_at`, which is NOT a total order — two rules
 * created in the same transaction, or the same clock tick, can come back either
 * way round on different polls. Ordering carries no meaning for a set of
 * standing instructions, so sorting costs nothing and removes a whole class of
 * flapping.
 *
 * ── WHY NOT HASH CLAUDE.md ITSELF ──
 *
 * The obvious shortcut, and it is wrong. CLAUDE.md also carries the skills
 * index, the integrations section and the guardrails, so keying on the file
 * would respawn every session on every unrelated regeneration. Same reasoning
 * that keeps ENG-5537's value-only drift watcher away from Composio URL
 * rotation.
 */
export function agentRulesRestartBasis(rules: readonly AgentRule[]): string {
  return [...rules]
    .map((r) => r.text.trim())
    .filter((t) => t.length > 0)
    .sort()
    // A separator that cannot appear in a rule body, so two rules cannot be
    // concatenated into a basis that collides with one longer rule.
    .join('\u0000');
}

/**
 * ENG-9861: does this agent have any standing rules at all?
 *
 * Governs the FIRST-POLL branch of the restart decision only, and it is the
 * direct analogue of `isSenderPolicyRestrictive`. An unknown baseline means the
 * manager genuinely does not know what the running session is operating under
 * (transition deploy, lost sidecar, brand-new agent):
 *
 *   no rules  → nothing to deliver, so establish the baseline and never
 *               restart. A fleet-wide respawn on the deploy that ships the
 *               sidecar would be pure churn.
 *   has rules → we cannot tell whether the live session has them, and a rule
 *               the person was told is in force may not be. Restart once.
 *
 * Fail-closed is cheap here specifically because rules are new: almost no agent
 * holds one yet, so the ship-day cost is close to zero. It gets more expensive
 * as adoption grows, which is an argument for landing it now rather than later.
 */
export function agentHasRules(rules: readonly AgentRule[]): boolean {
  return rules.some((r) => r.text.trim().length > 0);
}

/**
 * Render the rules block for CLAUDE.md.
 *
 * Returns '' for an agent with no rules — an empty section would spend budget
 * on a heading that says nothing, and every character here is charged on every
 * request forever.
 *
 * The wording tells the agent what a rule IS, not merely that it must obey.
 * These are instructions, not enforcement: an agent can fail to follow them and
 * nothing catches that, which is the honest difference between this and a
 * guardrail. Saying so here stops "MUST" being read as a hard block it is not.
 */
export function buildAgentRulesSection(rules: readonly AgentRule[]): string {
  const kept = rules
    .map((r) => r.text.trim())
    .filter(Boolean)
    // CodeRabbit on #5308: enforce the LENGTH cap here too, not just the count.
    //
    // My first version trimmed and capped the count, so a caller bypassing
    // `validateAgentRule` could render eight arbitrarily long rules and blow the
    // resident-context budget — the exact thing MAX_AGENT_RULES_CHARS claims
    // cannot happen. The "overfed" test missed it because it overfed on COUNT
    // with rules of exactly the maximum length, exercising the safe side of the
    // boundary it was named after.
    //
    // DROPPED, NOT TRUNCATED, and that is the interesting choice. Truncating a
    // rule mid-sentence can invert its meaning — "never email external domains
    // without approval" cut at 200 characters could read "never email external
    // domains", or worse, "never email". A half-instruction the agent will
    // faithfully obey is more dangerous than a missing one, and this path is
    // unreachable for a rule that went through validation, so the only rules it
    // discards are ones that should never have been stored.
    .filter((t) => t.length <= MAX_AGENT_RULE_CHARS)
    .slice(0, MAX_AGENT_RULES);
  if (kept.length === 0) return '';

  const lines = kept.map((t) => `- ${t}`).join('\n');
  return `${AGENT_RULES_SECTION_START}
## Standing rules from your people

Rules your team has asked you to follow every time, in their words. They are not
task-specific — they apply on every turn regardless of what you are doing. If one
ever conflicts with a guardrail above, the guardrail wins and you should say so
rather than silently picking one.

${lines}
${AGENT_RULES_SECTION_END}

`;
}
